import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './SistemaPage.css';

const API = process.env.REACT_APP_API_URL || '/api';

const JOB_STEP_KEYS = {
  // Football pipeline
  '1. Scraping + procesamiento + entrenamiento': 'sistema.job_scraping',
  '2. Predicciones partidos futuros':            'sistema.job_predictions',
  '3. Fetch cuotas bookmakers':                  'sistema.job_fetch_odds',
  '4. Guardar cuotas + detectar value bets':     'sistema.job_value_bets',
  '5. Tennis pipeline (ATP)':                    'sistema.job_tennis',
  // Football update steps (old + new)
  'update_results/scraping':                     'sistema.job_scraping_results',
  'update_results/db_update':                    'sistema.job_db_update',
  'update_results/validate':                     'sistema.job_validate',
  'update_results/football/scraping':            'sistema.job_football_scraping',
  'update_results/football/db_update':           'sistema.job_football_db',
  'update_results/football/validate':            'sistema.job_football_validate',
  // Cron 6h — Tennis (update_results.py)
  'T1. Descargar CSVs ATP (año actual)':                               'sistema.job_t1_fetch_csv',
  'T2. Sincronizar BD + validar predicciones tenis (JeffSackmann)':    'sistema.job_t2_sync_tennis',
  'T3. Descargar resultados recientes ESPN (ultimos 30 dias)':          'sistema.job_t3_espn',
  'T4. Obtener proximos partidos ATP desde ESPN':                       'sistema.job_t4_fetch_futures',
  'T5. Generar predicciones para proximos partidos ATP':                'sistema.job_t5_predict_atp',
  'T6. Predicciones retroactivas para completados ESPN sin prediccion': 'sistema.job_t6_retro',
  // Legacy keys (old step names still in pipeline_runs DB)
  'T4. Generar predicciones para proximos partidos ATP':                'sistema.job_t4_predict_atp',
  'T5. Predicciones retroactivas para completados ESPN sin prediccion': 'sistema.job_t5_retro',
  // Cron 6h — Baseball (update_results.py)
  'B1. Descargar partidos MLB (año actual)':          'sistema.job_b1_fetch_mlb_yr',
  'B2. Sincronizar BD + validar predicciones baseball':'sistema.job_b2_sync_bb',
  'B3. Generar predicciones para proximos partidos MLB':'sistema.job_b3_predict_mlb',
  // Tennis full pipeline (tennis_pipeline.py)
  'T1. Descargar CSVs ATP':                    'sistema.job_t1_csv',
  'T2. Sincronizar BD + validar predicciones': 'sistema.job_t2_sync',
  'T3. Reentrenar modelo ATP':                 'sistema.job_t3_retrain',
  'T4. Fetch partidos futuros ATP (ESPN)':     'sistema.job_t4_futures',
  'T5. Predicciones partidos futuros':         'sistema.job_t5_predict',
  'T6. Fetch odds tenis':                      'sistema.job_t6_odds',
  'T7. Guardar odds + value bets':             'sistema.job_t7_save',
  // Baseball full pipeline (baseball_pipeline.py)
  'B1. Descargar partidos MLB':       'sistema.job_b1_fetch',
  'B2. Sincronizar BD MLB':           'sistema.job_b2_sync_db',
  'B3. Reentrenar modelo MLB':        'sistema.job_b3_retrain',
  'B4. Fetch odds MLB':               'sistema.job_b4_odds',
  'B5. Predicciones partidos futuros':'sistema.job_b5_predict',
  'B6. Guardar odds + value bets MLB':'sistema.job_b6_save',
};

function jobType(step) {
  if (step.startsWith('update_results/')) return 'update';
  return 'pipeline';
}

function groupRuns(runs) {
  const groups = [];
  let current = null;

  for (const run of runs) {
    const type = jobType(run.step);
    const ranAt = new Date(run.ran_at);
    const minuteDiff = current
      ? Math.abs(ranAt - new Date(current.startedAt)) / 60000
      : Infinity;

    if (!current || current.type !== type || minuteDiff > 30) {
      current = {
        type,
        startedAt: run.ran_at,
        steps: [],
        hasError: false,
      };
      groups.push(current);
    }
    current.steps.push(run);
    if (run.status === 'error') current.hasError = true;
  }

  return groups;
}

function fmtDate(iso, locale) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(locale || undefined, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDur(secs) {
  if (secs == null) return '—';
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
}

function RunGroup({ group }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const overallStatus = group.hasError ? 'error' : 'ok';
  const typeLabel = group.type === 'pipeline'
    ? t('sistema.run_pipeline')
    : t('sistema.run_update');
  const totalDur = group.steps.reduce((s, r) => s + (r.duration || 0), 0);

  return (
    <div className={`run-group run-group--${overallStatus}`}>
      <button className="run-group__header" onClick={() => setOpen(o => !o)}>
        <span className={`run-badge run-badge--${overallStatus}`}>
          {overallStatus === 'ok' ? '✓' : '✗'}
        </span>
        <span className="run-group__type">{typeLabel}</span>
        <span className="run-group__date">{fmtDate(group.startedAt, i18n.language)}</span>
        <span className="run-group__dur">{fmtDur(totalDur)}</span>
        <span className="run-group__chevron">{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="run-group__steps">
          {group.steps.map((step, i) => (
            <div key={i} className={`run-step run-step--${step.status}`}>
              <span className={`run-step__dot run-step__dot--${step.status}`} />
              <span className="run-step__name">
                {t(JOB_STEP_KEYS[step.step] || step.step, { defaultValue: step.step })}
              </span>
              <span className="run-step__dur">{fmtDur(step.duration)}</span>
              {step.error && (
                step.error.includes('\n') ? (
                  <details className="run-step__error-details">
                    <summary className="run-step__error-summary">
                      {step.error.split('\n')[0]}
                    </summary>
                    <pre className="run-step__error-full">{step.error}</pre>
                  </details>
                ) : (
                  <span className="run-step__error">{step.error}</span>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function SistemaPage() {
  const { t, i18n } = useTranslation();
  const [runs, setRuns]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchRuns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/pipeline/runs?limit=100`);
      setRuns(res.data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(t('sistema.error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  const groups = groupRuns(runs);
  const lastOk     = runs.find(r => r.status === 'ok');
  const lastError  = runs.find(r => r.status === 'error');
  const updateRuns = runs.filter(r => r.step.startsWith('update_results/'));
  const pipeRuns   = runs.filter(r => !r.step.startsWith('update_results/'));

  return (
    <div className="sistema-page">
      <div className="sistema-header">
        <div>
          <h2 className="sistema-title">{t('sistema.title')}</h2>
          {lastRefresh && (
            <p className="sistema-subtitle">
              {t('sistema.last_refresh')} {fmtDate(lastRefresh.toISOString(), i18n.language)}
            </p>
          )}
        </div>
        <button className="sistema-refresh" onClick={fetchRuns} disabled={loading}>
          {loading ? t('sistema.loading') : t('sistema.refresh')}
        </button>
      </div>

      <div className="sistema-cards">
        <div className="sistema-card">
          <div className="sistema-card__label">{t('sistema.card_last_run')}</div>
          <div className="sistema-card__value sistema-card__value--ok">
            {lastOk ? fmtDate(lastOk.ran_at, i18n.language) : '—'}
          </div>
        </div>
        <div className="sistema-card">
          <div className="sistema-card__label">{t('sistema.card_updates')}</div>
          <div className="sistema-card__value">{updateRuns.length / 3 | 0}</div>
        </div>
        <div className="sistema-card">
          <div className="sistema-card__label">{t('sistema.card_pipelines')}</div>
          <div className="sistema-card__value">{pipeRuns.length / 5 | 0}</div>
        </div>
        <div className="sistema-card">
          <div className="sistema-card__label">{t('sistema.card_last_error')}</div>
          <div className="sistema-card__value sistema-card__value--error">
            {lastError ? fmtDate(lastError.ran_at, i18n.language) : t('sistema.no_errors')}
          </div>
        </div>
      </div>

      <div className="sistema-cron-info">
        <div className="cron-badge cron-badge--update">
          <div>
            <strong>{t('sistema.cron_update_title')}</strong>
            <span>{t('sistema.cron_update_desc')}</span>
          </div>
        </div>
        <div className="cron-badge cron-badge--pipeline">
          <div>
            <strong>{t('sistema.cron_pipeline_title')}</strong>
            <span>{t('sistema.cron_pipeline_desc')}</span>
          </div>
        </div>
      </div>

      {error && <div className="sistema-error">{error}</div>}

      {!loading && !error && groups.length === 0 && (
        <div className="sistema-empty">
          <p>{t('sistema.no_runs')}</p>
          <p className="sistema-empty__sub">{t('sistema.no_runs_sub')}</p>
        </div>
      )}

      <div className="run-list">
        {groups.map((g, i) => <RunGroup key={i} group={g} />)}
      </div>
    </div>
  );
}
